import { quadSideLengths, type Pt } from './projection';

// Geometry for CLIENT-SIDE crops: the dashboard renders the unit's region out
// of the camera's freshest FULL frame instead of waiting for the (often
// lagging) stored crop. Mirrors the jetson's cropping rules:
//  • a single 4-point polygon → perspective warp (fix_perspective_and_crop /
//    _crop_table_polygon), aspect = avg horizontal / avg vertical side length;
//  • anything else (multi-polygon queues, free-form tables) → the axis-aligned
//    bounding box of all polygons (_crop_from_image's AABB fallback).
// All inputs are normalized 0..1 image coords (PolygonArea.direction_vectors).

export interface NormBox { x0: number; y0: number; x1: number; y1: number }

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

/** Single 4-vertex polygon → its points; otherwise null (bbox crop). */
export function warpQuad(polygons: number[][][] | null | undefined): Pt[] | null {
  if (!polygons || polygons.length !== 1) return null;
  const poly = polygons[0];
  if (!poly || poly.length !== 4) return null;
  return poly.map(([x, y]) => [clamp01(x), clamp01(y)] as Pt);
}

/** Axis-aligned bounding box (normalized) of all polygons' vertices. */
export function polysBBox(polygons: number[][][]): NormBox | null {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const poly of polygons) {
    for (const [x, y] of poly) {
      x0 = Math.min(x0, x); y0 = Math.min(y0, y);
      x1 = Math.max(x1, x); y1 = Math.max(y1, y);
    }
  }
  if (!Number.isFinite(x0) || x1 - x0 < 1e-4 || y1 - y0 < 1e-4) return null;
  return { x0: clamp01(x0), y0: clamp01(y0), x1: clamp01(x1), y1: clamp01(y1) };
}

/**
 * Aspect (w/h) the client-side crop will render at, for a frame with the given
 * aspect. Pure + synchronous — the justified layout can size crop tiles from
 * polygon data alone, before any image bytes arrive, and the answer never
 * changes when a fresher frame swaps in (same polygon → same aspect).
 */
export function feCropAspect(
  polygons: number[][][] | null | undefined,
  frameAspect: number,
): number | null {
  if (!polygons || polygons.length === 0) return null;
  const quad = warpQuad(polygons);
  if (quad) {
    // Pixel-space side lengths on a (frameAspect × 1) frame — any uniform frame
    // scale gives the same ratio.
    const px: Pt[] = quad.map(([x, y]) => [x * frameAspect, y] as Pt);
    const s = quadSideLengths(px);
    const pw = (s.top + s.bottom) / 2;
    const ph = (s.left + s.right) / 2;
    if (!(pw > 1e-6) || !(ph > 1e-6)) return null;
    return pw / ph;
  }
  const b = polysBBox(polygons);
  if (!b) return null;
  return ((b.x1 - b.x0) * frameAspect) / (b.y1 - b.y0);
}
